import React, {useEffect} from "react";
import {Box} from "@unibuddy/patron";
import {useQuery} from "react-apollo";
import {useBooleanFeatureFlag} from "ub-feature-flagging-react";

import {MentorCarousel} from "shared/Widgets/MentorCarousel/components/MentorCarousel/MentorCarousel";
import useAnalytics from "shared/AnalyticsNew/hooks/useAnalytics";
import {productSpec, sharedEvents} from "shared/AnalyticsNew/constants/productSpecification";
import {TRAFFIC_DRIVERS_PRODUCT_OPENED_EVENT_ENABLED} from "shared/constants";
import {useSourceTracking} from "shared/SourceTracking/useSourceTracking";
import {decodeFiltersParam, TrafficDriverFilters} from "../../../../util/decodeFiltersParam";

import {useUniversity} from "../../../modules/UniversityProvider/UniversityProvider";
import {getRandomMentorsListCarouselQuery} from "./getRandomMentorsListCarouselQuery";

const SUPPORTED_SEQUENCE_FILTERS = ["degree", "degree_level", "country"];

export function getRedirectUrl(widgetUrl: string, mentorId?: string) {
    if (mentorId !== undefined) {
        const path = `inbox/chatwith/${mentorId}`;
        try {
            const url = new URL(widgetUrl);
            url.searchParams.set("unibuddy", path);
            return url.toString();
        } catch (error) {
            console.log(error);
        }
    }
    return widgetUrl;
}

// Must be default so we can lazy load
export default function MentorCarouselWidget() {
    const {slug: universitySlug, widgetUrl, widgetUrls} = useUniversity();
    const {redirectWithSourceTracking} = useSourceTracking();

    const finalWidgetUrl = widgetUrl ?? widgetUrls?.[0]?.url ?? "";
    const urlParams = new URLSearchParams(window.location.search);
    const ubLang = urlParams.get("ub_lang");
    const filters: TrafficDriverFilters[] = decodeFiltersParam(urlParams.get("filters"));
    const {trackEvent} = useAnalytics();
    const isProductOpenedEventEnabled = useBooleanFeatureFlag(
        TRAFFIC_DRIVERS_PRODUCT_OPENED_EVENT_ENABLED.key,
        TRAFFIC_DRIVERS_PRODUCT_OPENED_EVENT_ENABLED.value,
    );
    const sequenceFilter = [];
    let domesticOrInternational = null;
    let applyDegreeFilter = false;

    filters.forEach(({key, valueList}: TrafficDriverFilters) => {
        if (!key || !Array.isArray(valueList) || valueList.length === 0) return;

        if (key === "domesticOrInternational") {
            domesticOrInternational = valueList[0];
            return;
        }

        const filterKeyMap: Record<string, string> = {
            level: "degree_level",
            degreeId: "degree",
            countryId: "country",
        };
        const filterKey = filterKeyMap[key];
        if (filterKey && SUPPORTED_SEQUENCE_FILTERS.includes(filterKey)) {
            sequenceFilter.push(...valueList.map((value) => ({field: filterKey, value})));

            if (filterKey === "degree") applyDegreeFilter = true;
        }
    });

    const {data, loading, error} = useQuery(getRandomMentorsListCarouselQuery, {
        variables: {
            universitySlug,
            limit: 5,
            offset: 0,
            sequenceFilter,
            applyDegreeFilter,
            domesticOrInternational,
            ubLang,
        },
        skip: !universitySlug,
    });

    useEffect(() => {
        if (isProductOpenedEventEnabled) {
            trackEvent(sharedEvents.productOpened.name, {
                name: productSpec.embeddedUniversityCarousel.NAME,
                universitySlug,
                ubLang,
            });
        }
    }, [isProductOpenedEventEnabled, trackEvent, ubLang, universitySlug]);

    if (error || !finalWidgetUrl) return <>An error occurred</>;
    if (!data?.sequenceFilterMentorList?.mentors?.length) return null;

    const mentors = data.sequenceFilterMentorList.mentors;

    const onChatNow = (mentorId: string, cardPosition: number | string) => {
        trackEvent(sharedEvents.carouselChatNowClicked.name, {
            universitySlug,
            cta: "Chat now",
            cardPosition,
            numberOfCards: mentors.length,
        });
        const redirectUrl = getRedirectUrl(finalWidgetUrl, mentorId);
        window.open(redirectWithSourceTracking(redirectUrl) as string, "_blank");
    };

    const onSeeMore = () => {
        trackEvent(sharedEvents.carouselSeeMoreClicked.name, {
            universitySlug,
            cta: "See more",
            numberOfCards: mentors.length,
        });
        const redirectUrl: string = getRedirectUrl(finalWidgetUrl);
        window.open(redirectWithSourceTracking(redirectUrl) as string, "_blank");
    };

    return (
        <Box paddingX="medium">
            <MentorCarousel
                loading={loading}
                onChatNow={onChatNow}
                onSeeMore={onSeeMore}
                mentors={mentors}
                isExternal={false}
            />
        </Box>
    );
}
